/**
 * @fileoverview A plant's week at a glance on a grid tile or a list row: its state
 * (Thriving, Stressed, At risk) and a small icon per kind of trouble, in one
 * badge. The state word says how the plant is doing; the icons say why.
 *
 * This replaced a coloured edge down the side of tiles, rows and cards. One
 * colour said only the worst verdict, so a week both warm and cold wore one
 * of them; split into bands it read as a sequence, top half then bottom.
 *
 * The icons are the plant's marks (the plants brief's; D40), the same as its card's
 * chips, one per icon: a danger in a filled badge, harm (a chilling night,
 * a snap or spike, downpours) and water in their colours, water last. A
 * reading that only slows the plant gets no icon, as it gets no chip. A
 * week with nothing to act on is the state alone.
 *
 * Where: grid tiles (PlantTile) and list rows (PlantRow).
 * API: none; the plant's week comes in (usePlantsBrief).
 * Words (en/es): plants.card.state.*; each mark's chip words are the brief's.
 *
 * @module PlantMarks
 */

import React from 'react';
import { cn } from '@/lib/utils';
import { TOKEN_SOLID, TOKEN_TEXT, type VerdictToken } from '@/shared/theme/verdictTokens';
import { adviceIcon } from './Verdict';
import { WeekStateLabel } from './WeekState';
import { WeekIcon } from '@/shared/components/WeekIcon';
import type { BriefWeek, WeekStatus } from '@/shared/api/plantsBrief';

/** One icon: the mark it stands for, its colour, and whether it is a danger. */
interface Mark {
  /** The mark's icon; icons are one per kind. */
  Icon: ReturnType<typeof adviceIcon>;
  /** Its colour, or null where the theme has none (wind). */
  token: VerdictToken | null;
  /** True for a danger: drawn in a filled badge. */
  urgent: boolean;
  /** What it stands for, in words, shown on pointing at it: "Chilly Tue night". */
  label: string;
}

/**
 * One icon per kind among the plant's marks, the most serious of each kind
 * kept. Kept to this file, so the file exports only its component and hot
 * reload keeps working.
 *
 * @param week - The plant's week, as the brief says it.
 * @returns The icons, and the week's state.
 */
function plantMarks(week: BriefWeek): { marks: Mark[]; status: WeekStatus } {
  const marks = new Map<Mark['Icon'], Mark>();
  // The brief's marks come dangers first and water last already.
  for (const mark of week.marks) {
    const Icon = adviceIcon(mark.iconKind, mark.code);
    if (!marks.has(Icon)) marks.set(Icon, { Icon, token: mark.token, urgent: mark.kind === 'danger', label: mark.chip });
  }
  return { marks: [...marks.values()], status: { state: week.state, token: week.token } };
}

/** What the badge is drawn from, and where it sits. */
interface PlantMarksProps {
  /** The plant's week (usePlantsBrief), or nothing: before it arrives, or for a sheltered or archived plant. */
  week: BriefWeek | null | undefined;
  /** On a picture the badge gets a shadow, so it reads against any photograph. */
  onPicture?: boolean;
  /** Placement classes from the tile or row. */
  className?: string;
}

/**
 * The badge: the state's word, then the icons, inside one border. Nothing
 * without a week.
 *
 * Hidden from screen readers, which hear the state in the tile's or row's
 * own label instead. A narrow tile with a long state and many icons wraps
 * the icons under the word rather than running off the picture.
 *
 * @param props - PlantMarksProps.
 */
export const PlantMarks: React.FC<PlantMarksProps> = ({ week, onPicture = false, className }) => {
  if (!week) return null;
  const { marks, status } = plantMarks(week);
  return (
    <span
      aria-hidden="true"
      className={cn(
        'inline-flex max-w-full flex-wrap items-stretch overflow-hidden rounded-md border bg-background font-sans text-[0.625rem] leading-none',
        onPicture ? 'border-transparent shadow-sm' : 'border-border',
        className,
      )}
    >
      <WeekIcon name={week.icon} className="my-auto ml-1 h-3 w-3 shrink-0" />
      <WeekStateLabel status={status} className="px-1.5 py-1 tracking-wide" iconClassName="h-3 w-3" />
      {marks.length > 0 && (
        <span className="flex items-center gap-px px-0.5">
          {marks.map(({ Icon, token, urgent, label }, i) => (
            <span
              key={i}
              // Its words on pointing at it: the icons alone had none.
              title={label}
              className={cn(
                'flex h-[1.125rem] w-[1.125rem] items-center justify-center rounded-full',
                urgent
                  ? (token ? TOKEN_SOLID[token] : 'bg-foreground text-background')
                  : (token ? TOKEN_TEXT[token] : 'text-foreground'),
              )}
            >
              <Icon className="h-3.5 w-3.5" />
            </span>
          ))}
        </span>
      )}
    </span>
  );
};
